import React, { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Stack } from '../stack/stack';

type Props = {
  title: string;
  steps: string[];
  currentStep: number;
};

const LoadingProgress = ({ title, steps, currentStep }: Props) => {
  const [displayText, setDisplayText] = useState(steps[currentStep]);

  // Keep track of the animation state: "fadeOut" or "fadeIn".
  const [animateState, setAnimateState] = useState('fadeIn');

  // Temporary text to hold the upcoming step's text when we fade out.
  const [incomingText, setIncomingText] = useState('');

  useEffect(() => {
    const newText = steps[currentStep];
    // If the text changed, start a fade out, then fade in with the new text.
    if (newText !== displayText) {
      setAnimateState('fadeOut');
      setIncomingText(newText);
    }
  }, [currentStep, steps, displayText]);

  // Once fade-out animation finishes, switch to the new text and fade in.
  const handleAnimationEnd = () => {
    if (animateState === 'fadeOut') {
      setDisplayText(incomingText);
      setAnimateState('fadeIn');
    }
  };

  const progressValue = ((currentStep + 1) / steps.length) * 100;

  return (
    <Component alignItems="center" direction="column" justifyContent="center">
      <BarContainer progress={progressValue}>
        <TitleText>{title}</TitleText>
      </BarContainer>
      <DescriptionContainer>
        <DescriptionText animateState={animateState} onAnimationEnd={handleAnimationEnd}>
          {displayText}
        </DescriptionText>
      </DescriptionContainer>
    </Component>
  );
};

// Keyframe animations for fading out to the left, and fading in from the right.
const fadeOutLeft = keyframes`
  0% {
    opacity: 1;
    transform: translateX(0);
  }
  100% {
    opacity: 0;
    transform: translateX(-20px);
  }
`;

const fadeInRight = keyframes`
  0% {
    opacity: 0;
    transform: translateX(20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Container for the entire component.
const Component = styled(Stack)`
  width: 100%;
  margin: 0 auto;
`;

// The loading-bar.
const BarContainer = styled.div<{ progress: number }>`
  position: relative;
  height: 1.5em;
  width: 100%;
  background-color: ${(props) => props.theme.color.brand.lightest};
  border-radius: 0.3em;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.12),
    0 1px 2px rgba(0, 0, 0, 0.24);
  overflow: hidden;
  isolation: isolate;

  &:after {
    content: '';
    position: absolute;
    width: ${(props) => `${props.progress}%`};
    top: 0;
    left: 0;
    height: 100%;
    background-color: ${(props) => props.theme.color.brand.dark};
    z-index: 1;
  }
`;

const TitleText = styled.span`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.8em;
  z-index: 2;
`;

// Container for the description (so it doesn't collapse when text changes).
const DescriptionContainer = styled.div`
  margin-top: 8px;
  min-height: 24px;
  position: relative;
  overflow: hidden;
`;

// The descriptive text itself with fade-out / fade-in animations.
const DescriptionText = styled.div<{ animateState: string }>`
  color: ${(props) => props.theme.color.gray.light};
  ${({ animateState }) =>
    animateState === 'fadeOut' &&
    css`
      animation: ${fadeOutLeft} 0.3s forwards;
    `}
  ${({ animateState }) =>
    animateState === 'fadeIn' &&
    css`
      animation: ${fadeInRight} 0.3s forwards;
    `}
`;

export { LoadingProgress };
